import React from "react";
import Card from "components/card";
import EventCard from "./EventCard";
import MobileAgendaView from "./MobileAgendaView";

const CalendarGrid = ({ currentDate, events, onEventClick, loading }) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const today = new Date();
    const isCurrentMonth =
        today.getMonth() === month && today.getFullYear() === year;

    // Create array of weeks
    const weeks = [];
    let currentWeek = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
        currentWeek.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
        if (currentWeek.length === 7) {
            weeks.push(currentWeek);
            currentWeek = [];
        }
        currentWeek.push(day);
    }

    // Add empty cells for days after the last day of month
    while (currentWeek.length < 7) {
        currentWeek.push(null);
    }
    weeks.push(currentWeek);

    const dayLabels = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    const getDateKey = (date) => {
        const localDate = new Date(date);
        if (Number.isNaN(localDate.getTime())) return null;
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, "0");
        const day = String(localDate.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Get events for a specific date
    const getEventsForDate = (day) => {
        if (!day) return [];
        const dateKey = getDateKey(new Date(year, month, day));
        return (dateKey && events[dateKey]) ? events[dateKey] : [];
    };

    if (loading) {
        return (
            <Card extra="rounded-[20px] p-4">
                <div className="flex items-center justify-center h-96">
                    <p className="text-gray-600 dark:text-gray-400">Loading calendar...</p>
                </div>
            </Card>
        );
    }

    return (
        <>
            {/* Mobile Agenda View - Shown on Mobile Only */}
            <div className="md:hidden">
                <Card extra="rounded-[20px] p-4">
                    <MobileAgendaView
                        currentDate={currentDate}
                        events={events}
                        onEventClick={onEventClick}
                    />
                </Card>
            </div>

            {/* Desktop/Tablet Grid View - Hidden on Mobile */}
            <div className="hidden md:block">
                <Card extra="rounded-[20px] p-4">
                    {/* Day Labels */}
                    <div className="grid grid-cols-7 gap-3 mb-4">
                        {dayLabels.map((day) => (
                            <div
                                key={day}
                                className="text-center font-bold text-navy-700 dark:text-white text-sm py-4 border-b border-gray-200 dark:border-gray-700"
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-3">
                        {weeks.map((week, weekIndex) =>
                            week.map((day, dayIndex) => {
                                const dateEvents = getEventsForDate(day);
                                const isToday = isCurrentMonth && day === today.getDate();
                                const isOtherMonth = !day;

                                return (
                                    <div
                                        key={`${weekIndex}-${dayIndex}`}
                                        className={`min-h-[160px] p-3 rounded-lg border-2 transition-all duration-200 ${isOtherMonth
                                            ? "bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800"
                                            : isToday
                                                ? "bg-brand-500/5 border-brand-500"
                                                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                                            } ${!isOtherMonth && "cursor-pointer hover:border-brand-300"}`}
                                    >
                                        {day && (
                                            <>
                                                {/* Day Number */}
                                                <div className="mb-3">
                                                    <span
                                                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${isToday
                                                            ? "bg-brand-500 text-white"
                                                            : "text-navy-700 dark:text-white"
                                                            }`}
                                                    >
                                                        {day}
                                                    </span>
                                                </div>

                                                {/* Events */}
                                                <div className="space-y-1.5 max-h-[115px] overflow-y-auto">
                                                    {dateEvents.slice(0, 3).map((event, idx) => (
                                                        <EventCard
                                                            key={event.id}
                                                            event={event}
                                                            onClick={() => onEventClick(event)}
                                                        />
                                                    ))}

                                                    {/* More indicator */}
                                                    {dateEvents.length > 3 && (
                                                        <button
                                                            onClick={() => onEventClick(dateEvents[0])}
                                                            className="text-xs font-semibold text-brand-500 hover:text-brand-600 pl-2 pt-0.5"
                                                        >
                                                            +{dateEvents.length - 3} more
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </Card>
            </div>
        </>
    );
};

export default CalendarGrid;
